import { Button } from "@/shared/ui/button";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { getAppOriginUrl } from "@/shared/lib/appBasePath";
import { supabase } from "@/shared/integrations/supabase/client";
import { trackEvent } from "@/shared/integrations/firebase/analytics";
import { ProgressIndicator } from "./signupModal/ProgressIndicator";
import { Step1PersonalInfo } from "./signupModal/Step1PersonalInfo";
import { Step2UserType } from "./signupModal/Step2UserType";
import { Step3PropertyAddress } from "./signupModal/Step3PropertyAddress";
import { Step4PropertyName } from "./signupModal/Step4PropertyName";
import { INITIAL_FORM_DATA, type SignupFormData } from "./signupModal/types";
import { isStepValid } from "./signupModal/validation";

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SignupModal({ isOpen, onClose }: SignupModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<SignupFormData>(INITIAL_FORM_DATA);

  const handleInputChange = (field: keyof SignupFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handlePropertyTypeChange = (value: string) => {
    handleInputChange("propertyType", value);
    handleInputChange("propertySubType", "");
  };

  const handleUserTypeSelect = (typeId: string) => {
    handleInputChange("userType", typeId);
    setTimeout(() => handleNext(), 300);
  };

  const stepValid = isStepValid(currentStep, formData);

  const handleSubmit = async () => {
    if (!stepValid) return;

    setLoading(true);
    setError("");

    try {
      const { data: authData, error: signupError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${getAppOriginUrl()}/`,
          data: {
            first_name: formData.firstName || "",
            last_name: formData.lastName || "",
            phone_number: formData.phoneNumber || "",
            user_type: formData.userType,
          },
        },
      });

      if (signupError) throw new Error(`Signup failed: ${signupError.message}`);
      if (!authData.user) throw new Error("User creation failed - no user returned");

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) throw new Error(`Sign in failed: ${signInError.message}`);

      trackEvent("sign_up", { method: "password", user_type: formData.userType });

      onClose();
      // Auth listener will handle the redirect after SIGNED_IN
    } catch (err: any) {
      setError(err.message || "An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-foreground text-center">
            Get Started with Asset Management
          </DialogTitle>
          <ProgressIndicator currentStep={currentStep} />
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mt-6">
          {currentStep === 1 && (
            <Step1PersonalInfo formData={formData} onChange={handleInputChange} />
          )}
          {currentStep === 2 && (
            <Step2UserType
              selectedUserType={formData.userType}
              onSelect={handleUserTypeSelect}
            />
          )}
          {currentStep === 3 && (
            <Step3PropertyAddress formData={formData} onChange={handleInputChange} />
          )}
          {currentStep === 4 && (
            <Step4PropertyName
              formData={formData}
              onChange={handleInputChange}
              onPropertyTypeChange={handlePropertyTypeChange}
            />
          )}

          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>

            {currentStep < 4 ? (
              <Button
                variant="default"
                onClick={handleNext}
                disabled={!stepValid}
                className="flex items-center gap-2"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleSubmit}
                disabled={!stepValid || loading}
                className="flex items-center gap-2"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Account & Continue
                {!loading && <ArrowRight className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
